import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import styled from 'styled-components'
import { SPACING, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'
import {
  INSIGHT_PRIMARY,
  INSIGHT_SCALE_COLORS,
  INSIGHT_CURSOR_FILL,
  INSIGHT_TOOLTIP_STYLE,
} from './insightColors'

interface ScaleQuestionChartProps {
  answers: number[]
  /** Upper bound of the rating scale (5 for general-purpose, 10 for ODI). Defaults to 5. */
  max?: number
  isPrinting: boolean
}

const StatBadge = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: ${INSIGHT_PRIMARY};
  margin-bottom: ${SPACING.md};
`

const NoData = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  text-align: center;
  padding: ${SPACING.xl} 0;
`

export function ScaleQuestionChart({ answers, max = 5, isPrinting }: ScaleQuestionChartProps) {
  if (answers.length === 0) {
    return <NoData>No responses yet</NoData>
  }

  const points = Array.from({ length: max }, (_, i) => i + 1)
  const data = points.map((v) => ({
    score: String(v),
    count: answers.filter((a) => a === v).length,
  }))

  const mean = answers.reduce((s, a) => s + a, 0) / answers.length

  return (
    <>
      <StatBadge>
        Avg {mean.toFixed(1)} / {max}
      </StatBadge>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
          <XAxis
            dataKey="score"
            tick={{ fontSize: TYPOGRAPHY.caption.fontSize }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: TYPOGRAPHY.caption.fontSize }}
            axisLine={false}
            tickLine={false}
          />
          {!isPrinting && (
            <Tooltip
              cursor={{ fill: INSIGHT_CURSOR_FILL }}
              {...INSIGHT_TOOLTIP_STYLE}
              formatter={(val: number) => [val, 'responses']}
            />
          )}
          <ReferenceLine
            x={String(Math.round(mean))}
            stroke={INSIGHT_PRIMARY}
            strokeDasharray="4 3"
            label={{
              value: `avg`,
              position: 'top',
              fontSize: TYPOGRAPHY.caption.fontSize,
              fill: INSIGHT_PRIMARY,
            }}
          />
          <Bar
            dataKey="count"
            radius={[3, 3, 0, 0]}
            isAnimationActive={!isPrinting}
            activeBar={false}
          >
            {data.map((_, i) => {
              // Map column index to the 5-colour palette so 1-10 ranges reuse
              // the same low→high gradient without needing a 10-colour palette.
              const colorIdx = Math.min(
                INSIGHT_SCALE_COLORS.length - 1,
                Math.floor((i * INSIGHT_SCALE_COLORS.length) / max)
              )
              return <Cell key={i} fill={INSIGHT_SCALE_COLORS[colorIdx]} />
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  )
}
